import React, { useEffect, useState } from 'react';
import { ChevronLeft, MessagesSquare, X } from 'lucide-react';
import ProfileChatLite from './ProfileChatLite';

const API = process.env.REACT_APP_BACKEND_URL;

const roleBadge = {
  admin: 'bg-tge-navy text-white',
  manager: 'bg-blue-600 text-white',
  counsellor: 'bg-emerald-600 text-white',
};

/** Floating team chat (bottom-right, admin/staff only). Shows who's online
 *  in real time and opens a live 1-to-1 chat with any teammate. */
export default function TeamChatWidget({ currentUserId }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [peer, setPeer] = useState(null);

  useEffect(() => {
    const ping = () => fetch(`${API}/api/presence/ping`, { method: 'POST', credentials: 'include' }).catch(() => {});
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/presence/online`, { credentials: 'include' });
        if (res.ok) setMembers((await res.json()).members || []);
      } catch { /* offline */ }
    };
    ping(); load();
    const id = setInterval(() => { ping(); load(); }, 20_000);
    return () => clearInterval(id);
  }, []);

  const onlineCount = members.filter((m) => m.online).length;

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="team-chat-widget">
      {open && (
        <div className="mb-3 w-80 bg-white border border-tge-navy/10 rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-tge-navy text-white px-4 py-3 flex items-center gap-2">
            {peer ? (
              <button onClick={() => setPeer(null)} className="hover:opacity-70" data-testid="team-chat-back">
                <ChevronLeft size={16} />
              </button>
            ) : (
              <MessagesSquare size={15} className="text-tge-gold" />
            )}
            <div className="text-sm font-semibold flex-1 truncate">
              {peer ? peer.name : 'Team chat'}
            </div>
            {!peer && <span className="text-[10px] text-emerald-300 font-semibold">{onlineCount} online</span>}
            <button onClick={() => { setOpen(false); setPeer(null); }} className="hover:opacity-70" data-testid="team-chat-close">
              <X size={15} />
            </button>
          </div>

          {peer ? (
            <div className="p-3">
              <ProfileChatLite targetUserId={peer.user_id} currentUserId={currentUserId} pollMs={4000} heightClass="h-64" />
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-tge-cream" data-testid="team-chat-members">
              {members.length === 0 && (
                <div className="px-4 py-6 text-xs text-tge-muted text-center">No teammates found.</div>
              )}
              {members.map((m) => (
                <button
                  key={m.user_id}
                  onClick={() => setPeer(m)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-tge-cream-soft transition text-left"
                  data-testid={`team-chat-member-${m.user_id}`}
                >
                  <span className="relative">
                    <span className="w-8 h-8 rounded-full bg-tge-gold/20 text-tge-gold font-serif-display flex items-center justify-center text-sm">
                      {(m.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${m.online ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-tge-navy truncate">{m.name}</span>
                    <span className="block text-[10px] text-tge-muted">{m.online ? 'Online now' : 'Offline'}</span>
                  </span>
                  <span className={`text-[9px] uppercase font-semibold rounded-full px-1.5 py-0.5 ${roleBadge[m.role] || 'bg-slate-500 text-white'}`}>
                    {m.role}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-13 h-13 p-3.5 rounded-full bg-tge-navy hover:bg-tge-navy/90 text-white shadow-xl transition flex items-center justify-center"
        data-testid="team-chat-toggle"
      >
        <MessagesSquare size={20} />
        {onlineCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white">
            {onlineCount}
          </span>
        )}
      </button>
    </div>
  );
}
